import { Component, Inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { ToasterService } from 'src/app/core/services/toastr.service';
import { customerService } from 'src/app/core/services/customer.service';
import { customerData } from 'src/app/models/customerData';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';

@Component({
  selector: 'megastore-admin-delivery-charges',
  templateUrl: './delivery-charges.component.html',
  styleUrls: ['./delivery-charges.component.scss']
})
export class DeliveryChargesComponent implements OnDestroy {
  deliveryChargesForm: FormGroup;
  submitted = false;
  destroy$: Subject<boolean> = new Subject<boolean>();
  enableCustomCharges = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { customerData: customerData },
    private dialog: MatDialogRef<DeliveryChargesComponent>,
    private formBuilder: FormBuilder,
    private _toasterService: ToasterService,
    private _customerService: customerService,
    private _spinnerService: SpinnerService,
    private _translateService: TranslateService
  ) {
    this.deliveryChargesForm = this.formBuilder.group({
      enableCustomCharges: [false],
      normalDeliveryCharge: [{ value: '', disabled: true }, [Validators.required, Validators.min(0)]],
      expressDeliveryCharge: [{ value: '', disabled: true }, [Validators.required, Validators.min(0)]]
    });

    // Initialize form with existing data if available
    if (this.data.customerData) {
      this.initializeForm();
    }
  }

  initializeForm(): void {
    // Check if customer has custom delivery charges enabled
    const hasCustomCharges = this.data.customerData.hasCustomDeliveryCharges || false;
    
    this.enableCustomCharges = hasCustomCharges;
    
    this.deliveryChargesForm.patchValue({
      enableCustomCharges: this.data.customerData.hasCustomDeliveryCharges,
      normalDeliveryCharge: this.data.customerData.normalDeliveryCharge || '',
      expressDeliveryCharge: this.data.customerData.expressDeliveryCharge || ''
    });

    if (hasCustomCharges) {
      this.enableDeliveryChargeFields();
    }
  }

  onToggleChange(event: MatSlideToggleChange): void {
    this.enableCustomCharges = event.checked;
    
    if (event.checked) {
      this.enableDeliveryChargeFields();
    } else {
      this.disableDeliveryChargeFields();
    }
  }

  enableDeliveryChargeFields(): void {
    this.deliveryChargesForm.get('normalDeliveryCharge')?.enable();
    this.deliveryChargesForm.get('expressDeliveryCharge')?.enable();
  }

  disableDeliveryChargeFields(): void {
    this.deliveryChargesForm.get('normalDeliveryCharge')?.disable();
    this.deliveryChargesForm.get('expressDeliveryCharge')?.disable();
    // this.deliveryChargesForm.patchValue({
    //   normalDeliveryCharge: '',
    //   expressDeliveryCharge: ''
    // });
  }

  onSubmit(): void {
    this.submitted = true;
    
    if (!this.enableCustomCharges) {
      // If custom charges are disabled, form is valid
      this.onFormValid();
      return;
    }

    // Enable fields temporarily for validation
    this.enableDeliveryChargeFields();
    
    if (this.deliveryChargesForm.valid) {
      this.onFormValid();
    }
  }

  onFormValid(): void {
    const formData = this.deliveryChargesForm.value;
    
    // Prepare update data
    const updateData: any = {};
    
    if (this.enableCustomCharges) {
      updateData.normalDeliveryCharge = Number(formData.normalDeliveryCharge);
      updateData.expressDeliveryCharge = Number(formData.expressDeliveryCharge);
      updateData.hasCustomDeliveryCharges = true;
    } else {
      updateData.normalDeliveryCharge = 0;
      updateData.expressDeliveryCharge = 0;
      updateData.hasCustomDeliveryCharges = false;
    }

    this._spinnerService.addToLoader('updateDeliveryCharges');
    this._customerService
      .updateCustomer(updateData, this.data.customerData._id || '')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: customerData) => {
          this._spinnerService.removeFromLoader('updateDeliveryCharges');
          this._toasterService.successToastr(
            this._translateService.instant('DeliveryChargesUpdatedSuccessfully')
          );
          this.dialog.close(res);
        },
        error: () => {
          this._spinnerService.removeFromLoader('updateDeliveryCharges');
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.complete();
    this.destroy$.unsubscribe();
  }
} 